import { useUnit } from 'effector-react'

import { Progress } from '@/components/ui/progress'
import { $messageSendingStates } from '@/models/chattingUI/chatSuggestions/messageSendingTracker.ts'
import { $chatsMap } from '@/models/chattingUI/chats/chats'

interface ActiveSending {
  fanId: number
  version: number
  suggestionIndex: number
  totalMessages: number
  sentMessages: number
  isSending: boolean
  error?: string
  fanName?: string
}

export const MessageSendingProgress = () => {
  const messageSendingStates = useUnit($messageSendingStates)
  const chatsMap = useUnit($chatsMap)

  const activeSendings: ActiveSending[] = []

  Object.entries(messageSendingStates).forEach(([key, fanState]) => {
    if (!fanState) return

    const [fanId, version] = key.split('-').map(Number)
    const chat = chatsMap[fanId]
    const fanName =
      chat?.withUser?.displayName || chat?.withUser?.name || `Fan ${fanId}`

    Object.entries(fanState).forEach(([suggestionIndexStr, progress]) => {
      if (progress.isSending) {
        activeSendings.push({
          fanId,
          version,
          suggestionIndex: Number(suggestionIndexStr),
          totalMessages: progress.totalMessages,
          sentMessages: progress.sentMessages,
          isSending: progress.isSending,
          error: progress.error,
          fanName,
        })
      }
    })
  })

  const totalMessages = activeSendings.reduce(
    (sum, sending) => sum + sending.totalMessages,
    0,
  )
  const sentMessages = activeSendings.reduce(
    (sum, sending) => sum + sending.sentMessages,
    0,
  )
  const overallProgress =
    totalMessages > 0 ? (sentMessages / totalMessages) * 100 : 0

  if (activeSendings.length === 0) {
    return null
  }

  return (
    <div className="bg-card border-primary-500 border-2 border-border rounded-lg shadow-2xl p-4 max-h-[300px] overflow-y-auto mb-2">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-4 h-4 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
        <h3 className="text-sm font-semibold text-text-title">
          Sending Messages
        </h3>
      </div>

      <div className="space-y-4">
        <div className="space-y-2">
          <div className="flex justify-between items-center">
            <span className="text-xs font-medium text-text-sub">
              Total Progress
            </span>
            <span className="text-xs font-medium text-text-sub">
              {sentMessages}/{totalMessages} ({Math.round(overallProgress)}%)
            </span>
          </div>
          <Progress
            value={overallProgress}
            className="h-2"
          />
        </div>

        {activeSendings.map((sending, index) => {
          const progress =
            sending.totalMessages > 0
              ? (sending.sentMessages / sending.totalMessages) * 100
              : 0

          return (
            <div
              key={`${sending.fanId}-${sending.version}-${sending.suggestionIndex}`}
              className="space-y-2"
            >
              <div className="flex justify-between items-center">
                <span className="text-xs font-medium text-text-sub">
                  {sending.fanName} - Suggestions
                </span>
                <span className="text-xs font-medium text-text-sub">
                  {sending.sentMessages}/{sending.totalMessages} (
                  {Math.round(progress)}%)
                </span>
              </div>
              <div className="relative h-1.5 w-full overflow-hidden rounded-full bg-secondary-50">
                <div
                  className="h-full bg-success-default transition-all duration-300"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
